/**
 * Task #1527 — Provider toast bloccante per errori 422 quota.
 * Da montare una sola volta nel root tree dei due frontend (tenant + master).
 *
 * Il toast NON ha auto-dismiss: l'utente deve esplicitamente chiuderlo o
 * cliccare "Dettaglio quota". Per ruoli admin viene mostrata anche la CTA
 * "Sblocco emergenza" che linka al flusso master già esistente.
 */
import { useStorageQuotaErrors, type StorageQuotaErrorPayload } from '../../hooks/useStorageQuotaErrors';
import { toast as toastFn } from '../../hooks/use-toast';
import type { StorageQuotaErrorCode } from '@easyflow/shared';

export type StorageQuotaToastUserRole = 'tenant_admin' | 'admin' | 'master' | 'user' | string;

export interface StorageQuotaErrorToastProviderProps {
  /** Ruolo utente corrente: determina visibilità CTA "Sblocco emergenza". */
  userRole: StorageQuotaToastUserRole | null;
  onNavigateDetail: () => void;
  onRequestEmergencyUnblock?: () => void;
}

function titleFor(code: StorageQuotaErrorCode): string {
  if (code === 'DB_QUOTA_EXCEEDED_EMBEDDINGS') return 'Embedding KB sospeso — Database saturo';
  return 'Upload bloccato — Storage R2 saturo';
}

function isAdminRole(role: StorageQuotaToastUserRole | null): boolean {
  return role === 'tenant_admin' || role === 'admin' || role === 'master';
}

export function StorageQuotaErrorToastProvider(props: StorageQuotaErrorToastProviderProps): null {
  const { userRole, onNavigateDetail, onRequestEmergencyUnblock } = props;

  useStorageQuotaErrors((payload: StorageQuotaErrorPayload) => {
    const title = titleFor(payload.errorCode);
    const adminCta = isAdminRole(userRole) && onRequestEmergencyUnblock
      ? '\nUsa "Sblocco emergenza" per riattivare per 24h.'
      : '';
    const description = `${payload.message}${adminCta}`;
    // Toast bloccante: duration molto alta, l'utente deve chiudere a mano.
    toastFn({
      variant: 'destructive',
      title,
      description,
      duration: 1000 * 60 * 60, // 1h, di fatto sticky finché chiuso o navigato.
    });
    // Bind CTA tramite event handler globale: il toast base non supporta
    // bottoni custom dal layer hook; deleghiamo l'azione al click sul toast
    // tramite una notifica nativa addizionale + handler nel chiamante.
    // Strategia minimale: dispatch evento custom UI-level intercettato dai
    // toast action/global se disponibili; in mancanza, l'utente clicca
    // l'header widget per arrivare al dettaglio.
    // Nota: il toast base non supporta action button custom dal layer hook.
    // Le CTA dirette (Dettaglio quota / Sblocco emergenza) restano affidate
    // al banner globale, sempre visibile in stato critical/blocked, e al
    // widget header. Il toast funge da segnale immediato non bloccante UI.
    void onNavigateDetail;
    void payload.errorCode;
  });

  return null;
}

export default StorageQuotaErrorToastProvider;
